// src/client/game/ui/GameOverOverlay.ts

import Phaser from "phaser";
import type { RunStats } from "../core/model";

interface GameOverCallbacks {
  onPlayAgain: () => void;
  onMainMenu: () => void;
}

/**
 * Game Over overlay showing score summary and buttons.
 * Includes player score, high score (from Redis), and global rank.
 */
export class GameOverOverlay {
  private container: Phaser.GameObjects.Container;
  private background: Phaser.GameObjects.Rectangle;
  private titleText: Phaser.GameObjects.Text;
  private summaryText: Phaser.GameObjects.Text;

  private playAgainButton: Phaser.GameObjects.Rectangle;
  private playAgainLabel: Phaser.GameObjects.Text;
  private mainMenuButton: Phaser.GameObjects.Rectangle;
  private mainMenuLabel: Phaser.GameObjects.Text;

  private visible = false;

  constructor(
    private scene: Phaser.Scene,
    private readonly getRunStats: () => RunStats,
    private readonly getRankPosition: (score: number) => {
      rank: number;
      total: number;
    },
    private readonly callbacks: GameOverCallbacks
  ) {
    const { width, height } = scene.scale;

    this.container = scene.add.container(0, 0).setDepth(1000);

    // Slightly wider background panel for padding
    this.background = scene.add.rectangle(
      width / 2,
      height / 2,
      width * 0.78,
      height * 0.7,
      0x000000,
      0.85
    );

    this.titleText = scene.add
      .text(width / 2, height / 2 - height * 0.26, "Game Over", {
        fontFamily: "monospace",
        fontSize: "34px",
        color: "#ffffff",
      })
      .setOrigin(0.5);

    // Summary text with smaller font and tighter spacing
    this.summaryText = scene.add
      .text(width / 2, height / 2 - 40, "", {
        fontFamily: "monospace",
        fontSize: "17px",
        color: "#ffffff",
        align: "center",
        wordWrap: { width: width * 0.6 },
        lineSpacing: 4,
      })
      .setOrigin(0.5, 0.5);

    // Buttons with balanced horizontal padding
    const buttonWidth = 220;
    const buttonHeight = 46;
    const buttonSpacing = 14;
    const firstButtonY = height / 2 + 90;

    const makeYellowButton = (
      y: number,
      label: string,
      onClick: () => void
    ): { rect: Phaser.GameObjects.Rectangle; text: Phaser.GameObjects.Text } => {
      const rect = scene.add
        .rectangle(width / 2, y, buttonWidth, buttonHeight, 0xffd700, 1)
        .setStrokeStyle(2, 0x000000)
        .setInteractive({ useHandCursor: true });

      const text = scene.add
        .text(rect.x, rect.y, label, {
          fontFamily: "monospace",
          fontSize: "18px",
          color: "#000000",
        })
        .setOrigin(0.5);

      rect.on("pointerover", () => rect.setFillStyle(0xfff36b, 1));
      rect.on("pointerout", () => rect.setFillStyle(0xffd700, 1));
      rect.on("pointerup", async () => {
        this.hide();
        onClick();
      });

      return { rect, text };
    };

    const playAgain = makeYellowButton(
      firstButtonY,
      "Play Again",
      callbacks.onPlayAgain
    );
    const mainMenu = makeYellowButton(
      firstButtonY + buttonHeight + buttonSpacing,
      "Main Menu",
      callbacks.onMainMenu
    );

    this.playAgainButton = playAgain.rect;
    this.playAgainLabel = playAgain.text;
    this.mainMenuButton = mainMenu.rect;
    this.mainMenuLabel = mainMenu.text;

    this.container.add([
      this.background,
      this.titleText,
      this.summaryText,
      this.playAgainButton,
      this.playAgainLabel,
      this.mainMenuButton,
      this.mainMenuLabel,
    ]);

    this.hide();
  }

  async show(): Promise<void> {
    const stats = this.getRunStats();

    // Fetch rank and highscore from backend
    let highScore = 0;
    let rank = 1;
    let total = 1;
    try {
      const res = await fetch("/api/highscore");
      if (res.ok) {
        const data = await res.json();
        highScore = Number(data.highScore) || 0;
        rank = Number(data.rank) || 1;
        total = Number(data.total) || 1;
      }
    } catch (err) {
      console.warn("Failed to fetch highscore info", err);
    }

    const seconds = Math.floor(stats.timeMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const leftoverSec = seconds % 60;
    const timeStr =
      minutes > 0 ? `${minutes}m ${leftoverSec}s` : `${leftoverSec}s`;

    const kills = stats.enemiesKilled ?? 0;
    const rankStr = `${rank} / ${total}`;

    this.summaryText.setText(
      `Score: ${stats.score}\n` +
        `Time Alive: ${timeStr}\n` +
        `Laser Level: ${stats.laserLevel}\n` +
        `Kills: ${kills}\n` +
         `\n` +
        `High Score: ${highScore}\n`
    );

    this.container.setVisible(true);
    this.visible = true;

    // Save high score if new record
    if (stats.score > highScore) {
      try {
        await fetch("/api/highscore", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ score: stats.score }),
        });
      } catch (err) {
        console.warn("Failed to save highscore", err);
      }
    }
  }

  hide(): void {
    this.container.setVisible(false);
    this.visible = false;
  }

  clear(): void {
    this.container.destroy(true);
  }
}
